import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { ApiDescriptionProps } from "../../_models/APIDescription";
import "./ApiDescription.scss";

/** Display keeps FHIR canonical|version; href uses published HL7 HTML pages. */
const structureDefinitionHref = (canonical: string): string => {
  let base = canonical.split("|")[0]?.trim() || canonical;
  // US Core 5+ retired us-core-condition; Problems & Health Concerns is the 6.1 profile.
  if (base.endsWith("/StructureDefinition/us-core-condition")) {
    base = base.replace(
      "/us-core-condition",
      "/us-core-condition-problems-health-concerns"
    );
  }
  // There is no us-core-observation; Lab Observations is the primary Patient Access profile.
  if (base.endsWith("/StructureDefinition/us-core-observation")) {
    base = base.replace("/us-core-observation", "/us-core-observation-lab");
  }
  const usCore = base.match(
    /\/StructureDefinition\/(us-core-[A-Za-z0-9-]+)$/
  );
  if (usCore) {
    return `https://hl7.org/fhir/us/core/STU6.1/StructureDefinition-${usCore[1]}.html`;
  }
  const c4bb = base.match(/\/StructureDefinition\/(C4BB-[A-Za-z0-9-]+)$/);
  if (c4bb) {
    return `https://hl7.org/fhir/us/carin-bb/STU2/StructureDefinition-${c4bb[1]}.html`;
  }
  // US Core Implementation Guide (e.g. Patient $everything Profile)
  if (/ImplementationGuide-hl7\.fhir\.us\.core\/?$/i.test(base)) {
    return "https://hl7.org/fhir/us/core/STU6.1/ImplementationGuide-hl7.fhir.us.core.html";
  }
  return base;
};

export const ApiDescription = (props: ApiDescriptionProps) => {
  const urls = (props.url ?? "")
    .split(/\s*<br\s*\/?>\s*|\r?\n/i)
    .map((u) => u.trim())
    .filter(Boolean);

  return (
    <div className="page-layout api-layout overflow-x-hidden!">
      <div className="flex flex-col gap-6">
        {/* Hero */}
        <div className="relative rounded-2xl p-6 md:p-8 text-white bg-[linear-gradient(120deg,#041e79_0%,#0331a4_45%,#8c32b7_100%)] shadow-[0_12px_30px_rgba(4,30,121,0.18)]">
          <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0)_70%)]" />
          <div className="relative flex flex-col gap-6 justify-between md:flex-row md:items-center">
            <div className="flex flex-row items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                <ApiOutlinedIcon fontSize="large" />
              </div>
              <div>
                <p className="m-0 text-xs font-semibold uppercase opacity-85">
                  FHIR API Resource
                </p>
                <h2 className="m-0 mt-1 text-2xl font-bold leading-tight text-white normal-case">
                  {props.header}
                </h2>
              </div>
            </div>

            <div className="flex h-6 items-center rounded-full border border-white/25 bg-white/15 px-2.5 text-xs font-semibold text-white w-max">
              FHIR ENABLED
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="rounded-2xl border border-[#e3e7f3] bg-white">
          <div className="p-5 md:p-7">
            <div className="mb-3 flex flex-row items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e5ebff] text-(--darkblue)">
                <DescriptionOutlinedIcon fontSize="small" />
              </div>
              <h3 className="m-0 text-lg font-semibold normal-case">
                Description
              </h3>
            </div>
            <div
              id="desc"
              className="api-desc-content text-sm leading-relaxed text-(--black)"
              dangerouslySetInnerHTML={{ __html: props.description ?? "" }}
            />
          </div>
        </div>

        {/* Based on */}
        <div className="rounded-2xl border border-[#e3e7f3] bg-[linear-gradient(120deg,#ffffff_0%,#f7e6ff_60%,#e5ebff_100%)]">
          <div className="p-5 md:p-6">
            <div className="mb-4 flex flex-row items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-(--moderatevioletcustom) shadow-[0_6px_16px_rgba(140,50,183,0.15)]">
                <LinkOutlinedIcon fontSize="small" />
              </div>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-widest">
                  Based On
                </p>
                <h3 className="m-0 mt-0.5 text-base font-semibold normal-case">
                  HL7 FHIR Profile{urls.length > 1 ? "s" : ""}
                </h3>
              </div>
            </div>

            {urls.length > 0 ? (
              <div className="flex flex-col gap-2">
                {urls.map((url, index) => (
                  <a
                    key={index}
                    href={structureDefinitionHref(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-3 break-all rounded-lg border border-dashed border-(--moderatevioletcustom)/35 bg-white p-3 text-sm font-medium text-(--moderatevioletcustom) no-underline transition-all duration-200 hover:border-solid hover:border-(--moderatevioletcustom) hover:shadow-[0_6px_16px_rgba(140,50,183,0.15)]"
                  >
                    <span className="underline">{url}</span>
                    <LaunchOutlinedIcon
                      fontSize="small"
                      className="shrink-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
                    />
                  </a>
                ))}
              </div>
            ) : (
              <p className="m-0 rounded-lg border border-dashed border-(--moderatevioletcustom)/35 bg-white p-3 text-sm italic text-(--desaturateddarkblue)">
                URL not available
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
